import type { PaginatedResponse } from "types/DataTypes";

import { api } from "./api";
import {
  useCreatePartABPlanCoverageMutation,
  useCreatePartCDPlanCoverageMutation,
  useDeletePartABPlanCoverageMutation,
  useDeletePartCDPlanCoverageMutation,
  usePatchPartABPlanCoverageMutation,
  usePatchPartCDPlanCoverageMutation,
} from "./part-coverage-api";

type GetSurgicalPricingCalculationByIdArgs = {
  id: string | number;
};

type CreateSurgicalPricingCalculationArgs = {
  data: unknown;
};

type UpdateSurgicalPricingCalculationArgs = {
  id: string | number;
  data: unknown;
};

type GetMMCsByInputArgs = {
  search?: string;
  zipcode?: string;
  limit?: number;
  offset?: number;
};

type GetMMCsResponse = {
  results: Array<{
    id: number | null;
    zipcode: string;
    mmc: number | null;
    mmc_name: string;
    mmc_state: string;
    created: string | null;
    modified: string | null;
  }>;
  count: number;
};

type GetPayHistoryArgs = {
  msa_id?: string | number;
  cost_projection_id?: string | number;
  document_management_file_id?: string | number;
};

type CreatePayHistoryArgs = {
  data: unknown;
};

type UpdatePayHistoryArgs = {
  id: string | number;
  data: unknown;
};

type DeletePayHistoryArgs = {
  id: string | number;
};

type SupportedPayHistoryProvider = {
  key: string;
  display_name: string;
};

type ExtractPayHistoryArgs = {
  insurer_key: string;
  files?: File[];
  file?: File;
  document?: number;
  documents?: number[];
  date_of_injury: number;
  claim_number: number;
  msa?: number;
  cost_projection?: number;
  document_management_file?: number;
};

type ExtractPayHistoryResponse = {
  job_id: number;
  task_id: string;
  task_status: string;
  medical?: unknown[];
  rx?: unknown[];
  date_of_injury?: string | null;
  claim_number?: string | null;
  unrecognized_files?: string[];
  parsed_files_count?: number;
};

type AnalyzePayHistoryArgs = {
  msa_id?: number;
  cost_projection_id?: number;
  document_management_file_id?: number;
};

type AnalyzePayHistoryResponse = {
  job_id: number;
  task_id: string;
  task_status: string;
};

type CopyMedicalRecordsFromMetadocArgs = {
  serviceId: string | number;
  serviceType: "msa" | "cost_projection";
  metadocId: number;
};

type CopyMedicalRecordsFromMetadocResponse = {
  detail: string;
};

type ChangeServiceStatusArgs = {
  serviceId: string | number;
  status: string;
};

type GetMedicalRecordIndexesArgs = {
  file_id: string;
  filter?: string;
  limit?: number;
  offset?: number;
};

type CreateMedicalRecordIndexArgs = {
  data: unknown;
};

type UpdateMedicalRecordIndexArgs = {
  id: string | number;
  data: unknown;
};

type DeleteMedicalRecordIndexArgs = {
  id: string | number;
};

// surgical pricing calculations API endpoints
export const surgicalPricingCalculationAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getSurgicalPricingCalculations: builder.query<unknown, void>({
      query: () => ({
        url: "api/v1/surgical-pricing-calculations/",
        method: "GET",
      }),
      providesTags: ["SurgicalPricingCalculation"],
    }),

    getSurgicalPricingCalculationById: builder.query<
      unknown,
      GetSurgicalPricingCalculationByIdArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/surgical-pricing-calculations/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [
        { type: "SurgicalPricingCalculation", id },
      ],
    }),

    createSurgicalPricingCalculation: builder.mutation<
      unknown,
      CreateSurgicalPricingCalculationArgs
    >({
      query: ({ data }) => ({
        url: "api/v1/surgical-pricing-calculations/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SurgicalPricingCalculation", "TreatmentAllocation"],
    }),

    updateSurgicalPricingCalculation: builder.mutation<
      unknown,
      UpdateSurgicalPricingCalculationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/surgical-pricing-calculations/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["SurgicalPricingCalculation", "TreatmentAllocation"],
    }),

    getMMCsByInput: builder.query<GetMMCsResponse, GetMMCsByInputArgs>({
      query: ({ search, zipcode, limit, offset }) => ({
        url: "api/v1/zipcode-mmc-associations/",
        method: "GET",
        params: {
          search,
          zipcode,
          limit,
          offset,
        },
      }),
      transformResponse: (
        response: PaginatedResponse<{
          id: number | null;
          zipcode: string;
          mmc: number | null;
          mmc_name: string;
          mmc_state: string;
          created: string | null;
          modified: string | null;
        }>,
      ) => {
        return {
          results: response.results.map((item) => ({
            id: item.id,
            zipcode: item.zipcode,
            mmc: item.mmc,
            mmc_name: item.mmc_name,
            mmc_state: item.mmc_state,
            created: item.created,
            modified: item.modified,
          })),
          count: response.count,
        };
      },
    }),
  }),
});

// medical and rx pay history endpoints
export const payHistoryAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getMedicalPayHistory: build.query<unknown, GetPayHistoryArgs>({
      query: ({ msa_id, cost_projection_id, document_management_file_id }) => ({
        url: `api/v1/medical-pay-history-items/${
          document_management_file_id
            ? `?document_management_file_id=${document_management_file_id}`
            : msa_id
              ? `?msa_id=${msa_id}`
              : `?cost_projection_id=${cost_projection_id}`
        }`,
        method: "GET",
      }),
      providesTags: ["MedicalPayHistory"],
    }),
    createMedicalPayHistory: build.mutation<unknown, CreatePayHistoryArgs>({
      query: ({ data }) => ({
        url: "api/v1/medical-pay-history-items/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["MedicalPayHistory", "MedicalRecordIndexes"],
    }),
    updateMedicalPayHistory: build.mutation<unknown, UpdatePayHistoryArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/medical-pay-history-items/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MedicalPayHistory", "MedicalRecordIndexes"],
    }),
    deleteMedicalPayHistory: build.mutation<void, DeletePayHistoryArgs>({
      query: ({ id }) => ({
        url: `api/v1/medical-pay-history-items/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MedicalPayHistory", "MedicalRecordIndexes"],
    }),
    getRxPayHistory: build.query<unknown, GetPayHistoryArgs>({
      query: ({ msa_id, cost_projection_id, document_management_file_id }) => ({
        url: `api/v1/rx-pay-history-items/${
          document_management_file_id
            ? `?document_management_file_id=${document_management_file_id}`
            : msa_id
              ? `?msa_id=${msa_id}`
              : `?cost_projection_id=${cost_projection_id}`
        }`,
        method: "GET",
      }),
      providesTags: ["RxPayHistory"],
    }),
    createRxPayHistory: build.mutation<unknown, CreatePayHistoryArgs>({
      query: ({ data }) => ({
        url: "api/v1/rx-pay-history-items/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RxPayHistory", "MedicalRecordIndexes"],
    }),
    updateRxPayHistory: build.mutation<unknown, UpdatePayHistoryArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/rx-pay-history-items/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["RxPayHistory", "MedicalRecordIndexes"],
    }),
    deleteRxPayHistory: build.mutation<void, DeletePayHistoryArgs>({
      query: ({ id }) => ({
        url: `api/v1/rx-pay-history-items/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["RxPayHistory", "MedicalRecordIndexes"],
    }),
    getSupportedPayHistoryProviders: build.query<
      SupportedPayHistoryProvider[],
      void
    >({
      query: () => ({
        url: "api/v1/medical-pay-history-items/supported-providers/",
        method: "GET",
      }),
    }),
    extractPayHistory: build.mutation<
      ExtractPayHistoryResponse,
      ExtractPayHistoryArgs
    >({
      query: ({
        insurer_key,
        files,
        file,
        document: docId,
        documents,
        date_of_injury,
        claim_number,
        msa,
        cost_projection,
        document_management_file,
      }) => {
        const formData = new FormData();
        formData.append("insurer_key", insurer_key);
        if (files && files.length > 0) {
          files.forEach((f) => formData.append("files", f));
        } else if (file) {
          formData.append("file", file);
        }
        if (docId != null) {
          formData.append("document", String(docId));
        }
        if (documents && documents.length > 0) {
          documents.forEach((d) => formData.append("documents", String(d)));
        }
        formData.append("date_of_injury", String(date_of_injury));
        formData.append("claim_number", String(claim_number));
        if (msa != null) {
          formData.append("msa", String(msa));
        }
        if (cost_projection != null) {
          formData.append("cost_projection", String(cost_projection));
        }
        if (document_management_file != null) {
          formData.append(
            "document_management_file",
            String(document_management_file),
          );
        }
        return {
          url: "api/v1/medical-pay-history-items/extract/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        "MedicalPayHistory",
        "RxPayHistory",
        "Documents",
        "MedicalRecordIndexes",
      ],
    }),
    deletePayHistoryDocument: build.mutation<void, { document_id: number }>({
      query: ({ document_id }) => ({
        url: "api/v1/medical-pay-history-items/delete-document/",
        method: "POST",
        body: { document_id },
      }),
      invalidatesTags: [
        "MedicalPayHistory",
        "RxPayHistory",
        "Documents",
        "MedicalRecordIndexes",
      ],
    }),
    analyzePayHistory: build.mutation<
      AnalyzePayHistoryResponse,
      AnalyzePayHistoryArgs
    >({
      query: (body) => ({
        url: "api/v1/medical-pay-history-items/analyze/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "MedicalPayHistory",
        "RxPayHistory",
        "MedicalRecordIndexes",
      ],
    }),
  }),
  overrideExisting: false,
});

export const changeServiceStatus = api.injectEndpoints({
  endpoints: (build) => ({
    changeServiceStatus: build.mutation<unknown, ChangeServiceStatusArgs>({
      query: ({ serviceId, status }) => ({
        url: `api/v1/services/${serviceId}/change_status/`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LCP",
        "CostProjectionValidation",
        "AmendedReviewScreening",
        "AmendedReviewScreeningValidation",
        "Documents",
        "LienFile",
        "DocumentManagementFiles",
        "Files",
      ],
    }),
  }),
  overrideExisting: false,
});

// medical record index endpoints
export const medicalRecordIndexAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getMedicalRecordIndexes: build.query<unknown, GetMedicalRecordIndexesArgs>({
      query: ({ file_id, filter, limit, offset }) => {
        const queryParams = new URLSearchParams();

        queryParams.append("file", file_id);

        if (filter) {
          queryParams.append("filter", filter);
        }

        if (limit) {
          queryParams.append("limit", limit.toString());
        }

        if (offset) {
          queryParams.append("offset", offset.toString());
        }

        return {
          url: `api/v1/medical-record-indexes/?${queryParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["MedicalRecordIndexes"],
    }),
    createMedicalRecordIndex: build.mutation<
      unknown,
      CreateMedicalRecordIndexArgs
    >({
      query: ({ data }) => ({
        url: "api/v1/medical-record-indexes/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["MedicalRecordIndexes"],
    }),
    updateMedicalRecordIndex: build.mutation<
      unknown,
      UpdateMedicalRecordIndexArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/medical-record-indexes/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MedicalRecordIndexes"],
    }),
    deleteMedicalRecordIndex: build.mutation<
      void,
      DeleteMedicalRecordIndexArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/medical-record-indexes/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MedicalRecordIndexes"],
    }),
    copyMedicalRecordsFromMetadoc: build.mutation<
      CopyMedicalRecordsFromMetadocResponse,
      CopyMedicalRecordsFromMetadocArgs
    >({
      query: ({ serviceId, serviceType, metadocId }) => ({
        url:
          serviceType === "cost_projection"
            ? `api/v1/cost-projections/${serviceId}/copy-medical-records-from-metadoc/`
            : `api/v1/msas/${serviceId}/copy-medical-records-from-metadoc/`,
        method: "POST",
        body: { metadoc_id: metadocId },
      }),
      invalidatesTags: ["MedicalRecordIndexes"],
    }),
  }),
  overrideExisting: false,
});

export const { useChangeServiceStatusMutation } = changeServiceStatus;

export const {
  useGetMedicalRecordIndexesQuery,
  useCreateMedicalRecordIndexMutation,
  useUpdateMedicalRecordIndexMutation,
  useDeleteMedicalRecordIndexMutation,
  useCopyMedicalRecordsFromMetadocMutation,
} = medicalRecordIndexAPI;

// re-export the part coverage hooks from part-coverage-api.ts
export {
  useCreatePartABPlanCoverageMutation,
  usePatchPartABPlanCoverageMutation,
  useDeletePartABPlanCoverageMutation,
  useCreatePartCDPlanCoverageMutation,
  usePatchPartCDPlanCoverageMutation,
  useDeletePartCDPlanCoverageMutation,
};

export const {
  useGetSurgicalPricingCalculationsQuery,
  useGetSurgicalPricingCalculationByIdQuery,
  useCreateSurgicalPricingCalculationMutation,
  useUpdateSurgicalPricingCalculationMutation,
  useGetMMCsByInputQuery,
} = surgicalPricingCalculationAPI;

export const {
  useGetMedicalPayHistoryQuery,
  useCreateMedicalPayHistoryMutation,
  useUpdateMedicalPayHistoryMutation,
  useDeleteMedicalPayHistoryMutation,
  useGetRxPayHistoryQuery,
  useCreateRxPayHistoryMutation,
  useUpdateRxPayHistoryMutation,
  useDeleteRxPayHistoryMutation,
  useGetSupportedPayHistoryProvidersQuery,
  useExtractPayHistoryMutation,
  useDeletePayHistoryDocumentMutation,
  useAnalyzePayHistoryMutation,
} = payHistoryAPI;
